import type { GroupProps } from '@mantine/core';
import {
  Box,
  Button,
  Center,
  Group,
  Loader,
  Menu,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import {
  IconArrowBackUp,
  IconArrowRight,
  IconBell,
  IconBellOff,
  IconCaretDownFilled,
  IconDotsVertical,
  IconEdit,
  IconExclamationCircle,
  IconEye,
  IconEyeOff,
  IconFlag,
  IconId,
  IconLink,
  IconPinned,
  IconPinnedOff,
  IconShieldOff,
  IconTrash,
} from '@tabler/icons-react';
import { useClipboard } from '@mantine/hooks';
import clsx from 'clsx';
import React, { useState } from 'react';
import { useScrollToHighlightedComment } from '~/components/CommentsV2/Comment/useScrollToHighlightedComment';
import { create } from 'zustand';
import { CommentBadge } from '~/components/CommentsV2/Comment/CommentBadge';
import {
  CommentsProvider,
  useCommentsContext,
  useNewCommentStore,
  useRootThreadContext,
  useSeededReplyThreads,
} from '~/components/CommentsV2/CommentsProvider';
import { DaysFromNow } from '~/components/Dates/DaysFromNow';
import { openReportModal } from '~/components/Dialog/triggers/report';
import { BlockUserButton } from '~/components/HideUserButton/BlockUserButton';
import { HideUserButton } from '~/components/HideUserButton/HideUserButton';
import { LegacyActionIcon } from '~/components/LegacyActionIcon/LegacyActionIcon';
import { LineClamp } from '~/components/LineClamp/LineClamp';
import { LoginRedirect } from '~/components/LoginRedirect/LoginRedirect';
import { RenderHtml } from '~/components/RenderHtml/RenderHtml';
import { UserAvatar } from '~/components/UserAvatar/UserAvatar';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { ReportEntity } from '~/shared/utils/report-helpers';
import { type Comment as CommentModel } from '~/server/services/commentsv2.service';
import { closeAllModals, openConfirmModal } from '@mantine/modals';
import { showSuccessNotification } from '~/utils/notifications';
import { trpc } from '~/utils/trpc';
import { constants } from '../../../server/common/constants';
import { useMutateComment } from '../commentv2.utils';
import {
  HiddenCommentAvatar,
  HiddenCommentLabel,
  HideAgainButton,
  useHiddenCommentReveal,
} from './HiddenComment';
import classes from './Comment.module.css';
import { CommentForm } from './CommentForm';
import { CommentProvider, useCommentV2Context } from './CommentProvider';
import { CommentReactions } from './CommentReactions';
import { CreateComment } from './CreateComment';
import { DeleteComment } from './DeleteComment';

type Store = {
  id?: number;
  setId: (id?: number) => void;
};

const useStore = create<Store>((set) => ({
  setId: (id) => set(() => ({ id })),
}));

type CommentProps = Omit<GroupProps, 'children'> & {
  comment: CommentModel;
  viewOnly?: boolean;
  highlight?: boolean;
  resourceOwnerId?: number;
  borderless?: boolean;
};

export function Comment({ comment, resourceOwnerId, ...groupProps }: CommentProps) {
  return (
    <CommentProvider comment={comment} resourceOwnerId={resourceOwnerId}>
      <CommentContent comment={comment} {...groupProps} />
    </CommentProvider>
  );
}

const trimmableEnds = ['<p></p>'];

export function CommentContent({
  comment,
  viewOnly,
  highlight: highlightProp,
  borderless,
  ...groupProps
}: CommentProps) {
  const { setExpanded, setRootThread, rootEntityType } = useRootThreadContext();
  const { entityId, entityType, highlighted, level } = useCommentsContext();
  const { canDelete, canEdit, canReply, canHide, canPin, badge, canReport } = useCommentV2Context();
  const { concealed, reveal, conceal } = useHiddenCommentReveal(comment.hidden);

  const seededThreads = useSeededReplyThreads();
  const seededThread = seededThreads.byCommentId.get(comment.id);
  const seededCount =
    seededThread?.commentCount ?? (seededThreads.childless.has(comment.id) ? 0 : undefined);

  const { data: replyCount = 0 } = trpc.commentv2.getCount.useQuery(
    { entityId: comment.id, entityType: 'comment' },
    { initialData: seededCount }
  );

  const id = useStore((state) => state.id);
  const setId = useStore((state) => state.setId);

  const currentUser = useCurrentUser();
  const clipboard = useClipboard();

  const {
    toggleHide,
    togglePinned,
    setTosViolation,
    settingTosViolation,
    toggleThreadMute,
    togglingThreadMute,
  } = useMutateComment();

  // Fetched only while the menu is open: the alternative is one query per comment on every feed.
  const [menuOpened, setMenuOpened] = useState(false);
  const { data: threadMute } = trpc.commentv2.getThreadMuted.useQuery(
    { commentId: comment.id },
    { enabled: menuOpened && !!currentUser }
  );

  const handleCopyLink = () => {
    // Resolve through /comments/<id> rather than patching the current URL: the latter keeps
    // whatever deep-link params (commentParentId, threadId) the page arrived with, so a link copied
    // from a notification thread stays rooted on the notification's comment instead of this one.
    clipboard.copy(`${window.location.origin}/comments/${comment.id}`);
    showSuccessNotification({ message: 'Comment link copied to clipboard' });
  };

  const handleRemoveAsTos = () => {
    openConfirmModal({
      title: 'Remove as ToS violation',
      children: (
        <Text size="sm">
          This flags the comment as a Terms of Service violation, actions any matching reports and
          notifies the author. Are you sure?
        </Text>
      ),
      centered: true,
      labels: { confirm: 'Remove as ToS violation', cancel: 'Cancel' },
      confirmProps: { color: 'red', loading: settingTosViolation },
      closeOnConfirm: false,
      onConfirm: () =>
        setTosViolation({ id: comment.id })
          .catch(() => null)
          .finally(() => closeAllModals()),
    });
  };

  const handleCopyId = () => {
    clipboard.copy(String(comment.id));
    showSuccessNotification({ message: 'Comment ID copied to clipboard' });
  };

  const editing = id === comment.id;
  const [replying, setReplying] = useState(false);

  const isHighlighted = highlighted === comment.id;

  useScrollToHighlightedComment(isHighlighted, comment.id);

  const maxDepth = constants.comments.getMaxDepth({ entityType: rootEntityType ?? entityType });
  // Subscribe per comment rather than to the whole set: opening one thread must not re-render
  // every other comment in the conversation.
  const expandOverride = useNewCommentStore((state) => state.expandOverrides[comment.id]);
  // A thread the page fetched up front is one the reader is meant to see open. Deriving it means
  // re-rooting the section can't carry this section's opened threads back out with it.
  const expandsByDefault = !!seededThread && seededThread.depth < maxDepth;
  const isExpanded = !viewOnly && (expandOverride ?? expandsByDefault);
  // Too deep to indent any further, so this one opens as its own thread instead of in place.
  const opensOwnThread = (level ?? 0) >= maxDepth && !isExpanded;
  const onToggleReplies = () => {
    if (opensOwnThread) setRootThread('comment', comment.id);
    else setExpanded(comment.id, !isExpanded);
  };

  for (const end of trimmableEnds) {
    if (comment.content.endsWith(end)) {
      comment.content = comment.content.slice(0, comment.content.lastIndexOf(end));
    }
  }

  return (
    <Group
      id={`comment-${comment.id}`}
      align="flex-start"
      wrap="nowrap"
      {...groupProps}
      gap="sm"
      className={clsx(groupProps.className, classes.groupWrap, {
        [classes.highlightedComment]: highlightProp || isHighlighted,
      })}
    >
      <Group gap="xs">
        {/* {replyCount > 0 && !viewOnly && !isExpanded && (
          <UnstyledButton onClick={onToggleReplies}>
            <IconArrowsMaximize size={16} />
          </UnstyledButton>
        )} */}
        {concealed ? (
          <HiddenCommentAvatar size={26} />
        ) : (
          <UserAvatar user={comment.user} size="sm" linkToProfile withShowcaseHat />
        )}
      </Group>

      <Stack gap={0} style={{ flex: 1 }}>
        <Group justify="space-between">
          {concealed ? (
            <HiddenCommentLabel onShow={reveal} />
          ) : (
            <Group gap={8} align="center">
              <UserAvatar
                user={comment.user}
                size="md"
                linkToProfile
                includeAvatar={false}
                withUsername
                badge={badge ? <CommentBadge {...badge} /> : null}
              />
              <Text c="dimmed" size="xs" mt={2}>
                <DaysFromNow date={comment.createdAt} />
              </Text>
              {comment.pinnedAt && (
                <ThemeIcon size="sm" color="orange">
                  <IconPinned size={16} stroke={2} />
                </ThemeIcon>
              )}
              {currentUser?.isModerator && comment.tosViolation && (
                <Tooltip label="Has TOS Violation">
                  <ThemeIcon color="orange" size="xs">
                    <IconExclamationCircle />
                  </ThemeIcon>
                </Tooltip>
              )}
            </Group>
          )}

          {/* CONTROLS */}
          <Menu position="bottom-end" withinPortal opened={menuOpened} onChange={setMenuOpened}>
            <Menu.Target>
              <LegacyActionIcon size="xs" variant="subtle">
                <IconDotsVertical size={14} />
              </LegacyActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {canDelete && (
                <>
                  <DeleteComment id={comment.id} entityId={entityId} entityType={entityType}>
                    {({ onClick }) => (
                      <Menu.Item
                        leftSection={<IconTrash size={14} stroke={1.5} />}
                        color="red"
                        onClick={onClick}
                      >
                        Delete comment
                      </Menu.Item>
                    )}
                  </DeleteComment>
                  {canEdit && (
                    <Menu.Item
                      leftSection={<IconEdit size={14} stroke={1.5} />}
                      onClick={() => setId(comment.id)}
                    >
                      Edit comment
                    </Menu.Item>
                  )}
                </>
              )}
              {canHide && (
                <Menu.Item
                  leftSection={
                    comment.hidden ? (
                      <IconEye size={14} stroke={1.5} />
                    ) : (
                      <IconEyeOff size={14} stroke={1.5} />
                    )
                  }
                  onClick={() => toggleHide({ id: comment.id, entityType, entityId })}
                >
                  {comment.hidden ? 'Unhide comment' : 'Hide comment'}
                </Menu.Item>
              )}
              {canPin && !comment.hidden && (
                <Menu.Item
                  leftSection={
                    comment.pinnedAt ? (
                      <IconPinnedOff size={14} stroke={1.5} />
                    ) : (
                      <IconPinned size={14} stroke={1.5} />
                    )
                  }
                  onClick={() => togglePinned({ id: comment.id, entityType, entityId })}
                >
                  {comment.pinnedAt ? 'Unpin comment' : 'Pin comment'}
                </Menu.Item>
              )}
              <HideUserButton as="menu-item" userId={comment.user.id} />
              <BlockUserButton userId={comment.user.id} as="menu-item" />
              {canReport && (
                <LoginRedirect reason="report-model">
                  <Menu.Item
                    leftSection={<IconFlag size={14} stroke={1.5} />}
                    onClick={() =>
                      openReportModal({
                        entityType: ReportEntity.CommentV2,
                        entityId: comment.id,
                      })
                    }
                  >
                    Report
                  </Menu.Item>
                </LoginRedirect>
              )}
              {currentUser && (
                // Already silenced from further up, so unmuting here would delete nothing and then
                // claim the notifications were back on. Says where the mute lives instead.
                <Menu.Item
                  leftSection={
                    threadMute?.muted ? (
                      <IconBell size={14} stroke={1.5} />
                    ) : (
                      <IconBellOff size={14} stroke={1.5} />
                    )
                  }
                  // Not merely cosmetic: the mutation toggles from DB state, so clicking a control
                  // labelled "Mute" before the query lands UNMUTES an already-muted thread.
                  disabled={
                    !threadMute ||
                    threadMute.viaAncestor ||
                    togglingThreadMute ||
                    !threadMute.canMute
                  }
                  onClick={() => toggleThreadMute({ commentId: comment.id }).catch(() => null)}
                >
                  {threadMute?.viaAncestor
                    ? 'Muted with the thread above'
                    : threadMute && !threadMute.canMute
                    ? 'No replies to mute yet'
                    : threadMute?.muted
                    ? 'Unmute this thread'
                    : 'Mute this thread'}
                </Menu.Item>
              )}
              <Menu.Item leftSection={<IconLink size={14} stroke={1.5} />} onClick={handleCopyLink}>
                Copy link
              </Menu.Item>
              {currentUser?.isModerator && (
                <Menu.Item leftSection={<IconId size={14} stroke={1.5} />} onClick={handleCopyId}>
                  Copy comment ID
                </Menu.Item>
              )}
              {currentUser?.isModerator && !comment.tosViolation && (
                <Menu.Item
                  color="red"
                  leftSection={<IconShieldOff size={14} stroke={1.5} />}
                  onClick={handleRemoveAsTos}
                >
                  Remove as ToS violation
                </Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
        </Group>
        {/* COMMENT / EDIT COMMENT */}
        <Stack style={{ flex: 1 }} gap={4}>
          {!editing ? (
            <>
              {!concealed && (
                <Box my={5}>
                  <LineClamp className="text-sm" lineClamp={3} variant="block">
                    <RenderHtml
                      html={comment.content}
                      allowCustomStyles={false}
                      withMentions
                      withProfanityFilter
                      allowStickers
                    />
                  </LineClamp>
                </Box>
              )}
              {/* COMMENT INTERACTION */}
              {!concealed && (
                <Group gap={4}>
                  <CommentReactions comment={comment} />
                  {comment.hidden && <HideAgainButton onClick={conceal} />}
                  {canReply && !viewOnly && (
                    <Button
                      variant="subtle"
                      radius="xl"
                      onClick={() => setReplying(true)}
                      size="compact-xs"
                      color="gray"
                    >
                      <Group gap={4}>
                        <IconArrowBackUp size={14} />
                        Reply
                      </Group>
                    </Button>
                  )}
                </Group>
              )}
            </>
          ) : (
            <CommentForm comment={comment} onCancel={() => setId(undefined)} autoFocus />
          )}
        </Stack>
        {isExpanded && <CommentReplies commentId={comment.id} replyCount={replyCount} />}
        {canReply && replying && (
          <Box pt="sm">
            <CreateComment
              autoFocus
              onCancel={() => setReplying(false)}
              replyToCommentId={comment.id}
              className={classes.replyInset}
              borderless={borderless}
            />
          </Box>
        )}
        {replyCount > 0 && !viewOnly && !isExpanded && (
          <Group align="flex-start" mt="xs">
            <Button
              variant="subtle"
              radius="xl"
              color="blue"
              size="sm"
              onClick={onToggleReplies}
              rightSection={
                opensOwnThread ? <IconArrowRight size={16} /> : <IconCaretDownFilled size={16} />
              }
            >
              {opensOwnThread
                ? `View ${replyCount} ${replyCount > 1 ? 'replies' : 'reply'}`
                : `Show ${replyCount} More`}
            </Button>
          </Group>
        )}
      </Stack>
      {replyCount > 0 && !viewOnly && (
        <UnstyledButton onClick={onToggleReplies} className={classes.repliesIndicator} />
      )}
    </Group>
  );
}

function CommentReplies({ commentId, replyCount }: { commentId: number; replyCount: number }) {
  const { level, badges } = useCommentsContext();
  const { setRootThread } = useRootThreadContext();
  const { resourceOwnerId } = useCommentV2Context();

  return (
    <Stack mt="md" className={classes.replyInset}>
      <CommentsProvider
        entityType="comment"
        entityId={commentId}
        badges={badges}
        limit={constants.comments.replyPageSize}
        level={(level ?? 0) + 1}
      >
        {({ data, created, isLoading, showMore }) =>
          isLoading ? (
            <Center>
              <Loader type="bars" />
            </Center>
          ) : (
            <Stack>
              {data?.map((comment) => (
                <Comment key={comment.id} comment={comment} resourceOwnerId={resourceOwnerId} />
              ))}
              {/* A thread too long to sit inline opens on its own rather than paging in place —
                  paging here grows an already-indented block with no end in sight, behind a
                  button that reads the same as the article's own "load more". */}
              {showMore && (
                <Group align="flex-start">
                  <Button
                    variant="subtle"
                    radius="xl"
                    color="blue"
                    size="sm"
                    onClick={() => setRootThread('comment', commentId)}
                    rightSection={<IconArrowRight size={16} />}
                  >
                    View all {replyCount} replies
                  </Button>
                </Group>
              )}
              {created.map((comment) => (
                <Comment key={comment.id} comment={comment} resourceOwnerId={resourceOwnerId} />
              ))}
            </Stack>
          )
        }
      </CommentsProvider>
    </Stack>
  );
}
